// ===========================================
// What a late-fee settings change MEANS, in words (#372 plan A6.1, old plan 3.7 / D14)
// ===========================================
// Pure. Used twice with the same function: BEFORE a save (the confirm dialog, from the frontend
// mirror `classifyLateFeeChange(saved, form)` with today as the start date) and AFTER it (the
// success alert, from the SERVER's `lateFeeChange`). Dates are dd/mm/yyyy, built from the
// `YYYY-MM-DD` key by string, so no timezone can move the day.

import type { LateFeeChangeKind, StricterReason } from '../types/adminSettings.types';

export interface LateFeeChangeContext {
  /** The start date the save gives (YYYY-MM-DD): today for ENABLED/STRICTER. */
  enabledFrom: string | null;
  /** "GST", "VAT" or the school's custom name; "tax" when there is none. */
  taxName: string;
  /** For LOOSER: switches that went off, said in words first. */
  turnedOff?: { partPaid?: boolean; tax?: boolean };
}

export interface LateFeeChangeDescription {
  title: string;
  paragraphs: string[];
  severity: 'warning' | 'info';
}

/** `YYYY-MM-DD` → `dd/mm/yyyy`, by string (a `@db.Date` key). */
export function dayKeyToDisplay(key: string | null): string {
  if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return '';
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
}

export function reasonWords(reason: StricterReason, taxName: string): string {
  switch (reason) {
    case 'DAYS_FEWER':
      return 'fewer days after the due date';
    case 'AMOUNT_HIGHER':
      return 'a higher amount';
    case 'PERCENT_HIGHER':
      return 'a higher percentage';
    case 'CAP_HIGHER':
      return 'a higher cap on each fee';
    case 'CAP_REMOVED':
      return 'no cap on each fee';
    case 'TYPE_CHANGED':
      return 'a different kind of fee (fixed / percentage)';
    case 'PART_PAID_ON':
      return 'part-paid invoices are now charged';
    case 'TAX_ON':
      return `${taxName} is now charged on the fee`;
    case 'REPEAT_ON':
      return 'repeating fees';
    case 'REPEAT_SOONER':
      return 'repeating fees sooner';
    case 'MAX_COUNT_HIGHER':
      return 'more fees on one invoice';
    case 'MAX_COUNT_REMOVED':
      return 'no limit on the number of fees';
    case 'MAX_TOTAL_HIGHER':
      return 'a higher most in fees on one invoice';
    case 'MAX_TOTAL_REMOVED':
      return 'no most in fees on one invoice';
    case 'MIN_OWING_LOWER':
      return 'a lower amount owing before a fee';
    case 'MIN_OWING_REMOVED':
      return 'no least amount owing before a fee';
    case 'KIND_ADDED':
      return 'more kinds of invoice charged';
    case 'GAP_SHORTER':
      return 'a shorter wait after a late Last Chance email';
  }
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

export function describeLateFeeChange(
  change: { kind: LateFeeChangeKind; reasons: StricterReason[] },
  ctx: LateFeeChangeContext
): LateFeeChangeDescription | null {
  const from = dayKeyToDisplay(ctx.enabledFrom);
  switch (change.kind) {
    case 'NONE':
      return null;
    case 'ENABLED':
      return {
        title: 'Late fees will be switched on',
        severity: 'warning',
        paragraphs: [
          `Late fees will apply only to invoices due on or after ${from}. Invoices already overdue won't be charged.`,
          'A fee is added only after the Last Chance reminder has reached the family.',
        ],
      };
    case 'STRICTER':
      return {
        title: 'This is a stricter late fee',
        severity: 'warning',
        paragraphs: [
          `This is stricter than your current late fee (${joinWords(change.reasons.map((r) => reasonWords(r, ctx.taxName)))}).`,
          `Families were warned about the old one, so invoices due before ${from} won't be charged a late fee at all. Invoices due from ${from} get the new fee.`,
        ],
      };
    case 'LOOSER': {
      const paragraphs: string[] = [];
      if (ctx.turnedOff?.partPaid) paragraphs.push("Part-paid invoices won't be charged a late fee.");
      if (ctx.turnedOff?.tax) {
        paragraphs.push(`${ctx.taxName} won't be charged on late fees added from now on; fees already added keep theirs.`);
      }
      paragraphs.push('This applies straight away to every invoice not yet charged.');
      return { title: 'Late fee settings changed', severity: 'info', paragraphs };
    }
    case 'DISABLED':
      return {
        title: 'Late fees will be switched off',
        severity: 'warning',
        paragraphs: [
          'No more late fees will be added. Fees already added stay on their invoices (waive them one by one if needed).',
          'If you switch late fees back on, they will apply only to invoices due from that day.',
        ],
      };
  }
}
